// Meta Pixel analytics utility functions for tracking events
import { PRICING } from "@/constants/pricing";

declare global {
  interface Window {
    fbq: (command: string, eventName: string, parameters?: Record<string, any>) => void;
  }
}

// Track which purchases have already been recorded to prevent duplicates
const trackedPurchases = new Set<string>();

// Wait for Meta Pixel to be ready
const waitForMetaPixel = async (): Promise<void> => {
  if (typeof window === "undefined") return;

  // If Meta Pixel is already loaded, return immediately
  if ("fbq" in window) {
    return;
  }

  // Wait for the script to load
  return new Promise((resolve) => {
    const checkMetaPixel = () => {
      if ("fbq" in window) {
        resolve();
      } else {
        setTimeout(checkMetaPixel, 100);
      }
    };
    checkMetaPixel();
  });
};

export const trackMetaPixelEvent = async (eventName: string, parameters?: Record<string, any>) => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    await waitForMetaPixel();

    // Ensure fbq is available
    if (typeof window.fbq === "function") {
      window.fbq("track", eventName, parameters);
    }
  } catch (error) {
    console.error("Error tracking Meta Pixel event:", error);
  }
};

// AI Color Analysis specific events
export const trackMetaPixelLandingPage = () => {
  trackMetaPixelEvent("ViewContent", {
    content_name: "AI Color Analysis Landing Page",
    content_category: "Color Analysis",
    content_type: "product",
    value: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    currency: "USD",
  });
};

export const trackMetaPixelAnalysisWizard = () => {
  trackMetaPixelEvent("ViewContent", {
    content_name: "AI Color Analysis Wizard",
    content_category: "Color Analysis",
    content_type: "product",
    value: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    currency: "USD",
  });
};

export const trackMetaPixelPurchaseClicked = () => {
  trackMetaPixelEvent("InitiateCheckout", {
    content_name: "AI Color Analysis",
    content_category: "Color Analysis",
    content_type: "product",
    value: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    currency: "USD",
    content_ids: ["ai_color_analysis"],
    num_items: 1,
  });
};

export const trackMetaPixelPaymentCompleted = (analysisId: string) => {
  // Check if this purchase has already been tracked
  if (trackedPurchases.has(analysisId)) {
    return;
  }

  trackMetaPixelEvent("Purchase", {
    content_name: "AI Color Analysis",
    content_category: "Color Analysis",
    content_type: "product",
    value: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    currency: "USD",
    content_ids: ["ai_color_analysis"],
    order_id: analysisId,
    num_items: 1,
  });

  // Mark this purchase as tracked
  trackedPurchases.add(analysisId);
};

export const trackMetaPixelAnalysisCompleted = (analysisId: string) => {
  trackMetaPixelEvent("CompleteRegistration", {
    content_name: "AI Color Analysis Results",
    content_category: "Color Analysis",
    content_type: "product",
    value: PRICING.AI_COLOR_ANALYSIS.BASE_PRICE,
    currency: "USD",
    content_ids: [analysisId],
  });
};

// Tool-first funnel events
export const trackToolFirstPageView = () => {
  trackMetaPixelEvent("ViewContent", {
    content_name: "AI Color Analysis Tool Page",
    content_category: "Color Analysis",
    content_type: "tool",
  });
};

export const trackUploadStarted = () => {
  trackMetaPixelEvent("AddToCart", {
    content_name: "Photo Upload Started",
    content_category: "Color Analysis",
    content_type: "tool_interaction",
  });
};

export const trackUploadCompleted = (photoCount: number) => {
  trackMetaPixelEvent("AddToWishlist", {
    content_name: "Photo Upload Completed",
    content_category: "Color Analysis",
    content_type: "tool_interaction",
    num_items: photoCount,
  });
};

export const trackBaselinePickerCompleted = (answers: Record<string, string | null>) => {
  trackMetaPixelEvent("CustomizeProduct", {
    content_name: "Baseline Picker Completed",
    content_category: "Color Analysis",
    content_type: "tool_interaction",
    ...answers,
  });
};

export const trackPreliminaryResultViewed = (colorDirection: string) => {
  trackMetaPixelEvent("Lead", {
    content_name: "Preliminary Result Viewed",
    content_category: "Color Analysis",
    content_type: "tool_result",
    color_direction: colorDirection,
  });
};

export const trackAdditionalPhotosAdded = (newCount: number, totalCount: number) => {
  trackMetaPixelEvent("AddToCart", {
    content_name: "Additional Photos Added",
    content_category: "Color Analysis",
    content_type: "tool_interaction",
    new_photos: newCount,
    total_photos: totalCount,
  });
};

// Test function to manually trigger Meta Pixel (for development only)
export const testMetaPixel = () => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof window.fbq === "function") {
    window.fbq("track", "PageView");
  }
};
